import { DEFAULT_SECTOR_NAMES } from './defaults';

export const MIN_WHEEL_SECTOR_COUNT = 2;
export const MAX_WHEEL_SECTOR_COUNT = 15;
export const MAX_SECTOR_NAME_LENGTH = 40;

/** Shared wheel structure synced over LiveKit between psychologist and client. */
export type WheelConfig = {
  values: number[];
  sectorNames: string[];
};

export function clampSectorCount(count: number): number {
  return Math.max(MIN_WHEEL_SECTOR_COUNT, Math.min(MAX_WHEEL_SECTOR_COUNT, count));
}

export function normalizeWheelValues(values: number[], sectorCount: number): number[] {
  return Array.from({ length: sectorCount }, (_, i) => {
    const value = values[i];
    if (typeof value !== 'number' || Number.isNaN(value)) return 5;
    return Math.max(0, Math.min(10, value));
  });
}

export function normalizeSectorNames(names: string[] | undefined | null, sectorCount: number): string[] {
  return Array.from({ length: sectorCount }, (_, i) => {
    const raw = names?.[i];
    const trimmed = typeof raw === 'string' ? raw.trim().slice(0, MAX_SECTOR_NAME_LENGTH) : '';
    return trimmed || DEFAULT_SECTOR_NAMES[i] || `Сектор ${i + 1}`;
  });
}

export function createDefaultWheelConfig(values?: number[]): WheelConfig {
  const sectorCount =
    values?.length && values.length >= MIN_WHEEL_SECTOR_COUNT && values.length <= MAX_WHEEL_SECTOR_COUNT
      ? values.length
      : DEFAULT_SECTOR_NAMES.length;
  return {
    values: normalizeWheelValues(values ?? Array.from({ length: sectorCount }, (_, i) => i + 1), sectorCount),
    sectorNames: normalizeSectorNames(DEFAULT_SECTOR_NAMES, sectorCount),
  };
}

export function normalizeWheelConfig(values: number[], sectorNames?: string[] | null): WheelConfig {
  const countFromNames = Array.isArray(sectorNames) ? sectorNames.length : 0;
  const countFromValues = Array.isArray(values) ? values.length : 0;
  const sectorCount = clampSectorCount(countFromNames || countFromValues || DEFAULT_SECTOR_NAMES.length);

  return {
    values: normalizeWheelValues(values ?? [], sectorCount),
    sectorNames: normalizeSectorNames(sectorNames, sectorCount),
  };
}

export function areWheelConfigsEqual(a: WheelConfig, b: WheelConfig): boolean {
  if (a.values.length !== b.values.length || a.sectorNames.length !== b.sectorNames.length) {
    return false;
  }

  return (
    a.values.every((value, index) => value === b.values[index]) &&
    a.sectorNames.every((name, index) => name === b.sectorNames[index])
  );
}

export function parseWheelConfigPayload(payload: { values?: unknown; sectorNames?: unknown }): WheelConfig | null {
  if (!Array.isArray(payload.values) || !payload.values.every((value) => typeof value === 'number')) {
    return null;
  }

  const sectorNames = Array.isArray(payload.sectorNames)
    ? payload.sectorNames.filter((name): name is string => typeof name === 'string')
    : undefined;

  return normalizeWheelConfig(payload.values, sectorNames);
}
